import { useState, useCallback, useMemo, useRef } from "react";
import {
  Table,
  Badge,
  Box,
  Group,
  ActionIcon,
  Autocomplete,
  Button,
  TextInput,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import type { SpecimenMacroForAccessioning } from "../../services/workflows.service";
import { CertaintySelect, CertaintyTextInput } from "./CertaintyInput";
import SmartSuggestion from "./SmartSuggestion";
import {
  MATERIAL_OPTIONS,
  CONSULTATION_MATERIAL_VALUES,
} from "./specimenConstants";

interface SourceOption {
  value: string;
  label: string;
  defaultCollectionMethodId?: string;
  defaultCollectionMethodName?: string;
}

const sanitizeOptions = <T extends { value: string | null | undefined }>(
  opts: T[] | undefined
): T[] => {
  if (!Array.isArray(opts)) return [];
  return opts.filter(
    (o) => typeof o.value !== "undefined" && o.value !== null && o.value !== ""
  );
};

interface SpecimenRowProps {
  specimen: any;
  index: number;
  form: UseFormReturnType<any>;
  fieldPrefix: string;
  sources: SourceOption[];
  subsites: Record<string, Array<{ value: string; label: string }>>;
  collectionMethods: Array<{ value: string; label: string }>;
  certainties: Record<string, number>;
  onCertaintyChange?: (field: string, certainty: number) => void;
  canDelete: boolean;
  onDeleteClick: () => void;
  onAddCollectionMethod: () => void;
  onSmartAddCollectionMethod?: () => void;
  unmatchedCollectionMethod?: string;
  unmatchedSubsite?: string;
  onAddSubsite?: (index: number, name: string) => void;
  onFindingsBlur?: () => void;
  specimenMacros: SpecimenMacroForAccessioning[];
  macroInputRef: (el: HTMLInputElement | null) => void;
  onFocusNextMacro: () => void;
  isConsultation?: boolean;
  identifierEditable?: boolean;
  identifierError?: string;
  onIdentifierChange?: (value: string) => void;
}

export default function SpecimenRow({
  specimen,
  index,
  form,
  fieldPrefix,
  sources,
  subsites,
  collectionMethods,
  certainties,
  onCertaintyChange,
  canDelete,
  onDeleteClick,
  onAddCollectionMethod,
  onSmartAddCollectionMethod,
  unmatchedCollectionMethod,
  unmatchedSubsite,
  onAddSubsite,
  onFindingsBlur,
  specimenMacros,
  macroInputRef,
  onFocusNextMacro,
  isConsultation,
  identifierEditable = false,
  identifierError,
  onIdentifierChange,
}: SpecimenRowProps) {
  const [macroInput, setMacroInput] = useState(specimen.macroCode || "");
  const [macroError, setMacroError] = useState(false);
  const newSubsiteNameRef = useRef("");

  const materialOptions = useMemo(
    () =>
      isConsultation
        ? MATERIAL_OPTIONS.filter((o) =>
            CONSULTATION_MATERIAL_VALUES.has(o.value)
          )
        : MATERIAL_OPTIONS,
    [isConsultation]
  );

  const availableSubsites = useMemo(
    () => subsites[specimen.sourceId] || [],
    [subsites, specimen.sourceId]
  );

  const macroSuggestions = useMemo(
    () =>
      specimenMacros
        .filter(
          (m) =>
            !macroInput ||
            m.code.toLowerCase().includes(macroInput.toLowerCase()) ||
            (m.description &&
              m.description.toLowerCase().includes(macroInput.toLowerCase()))
        )
        .map((m) => ({
          value: m.code,
          label: `${m.code} — ${m.description || ""}`,
        })),
    [specimenMacros, macroInput]
  );

  const applyMacro = useCallback(
    (macro: SpecimenMacroForAccessioning) => {
      setMacroInput(macro.code);
      setMacroError(false);
      form.setFieldValue(`${fieldPrefix}.macroCode`, macro.code);
      if (macro.sourceId) {
        form.setFieldValue(`${fieldPrefix}.sourceId`, macro.sourceId);
        onCertaintyChange?.(`${fieldPrefix}.sourceId`, 100);
      }
      if (macro.subsiteId) {
        form.setFieldValue(`${fieldPrefix}.subsiteId`, macro.subsiteId);
        onCertaintyChange?.(`${fieldPrefix}.subsiteId`, 100);
      }
      if (macro.findings) {
        form.setFieldValue(`${fieldPrefix}.findings`, macro.findings);
        onCertaintyChange?.(`${fieldPrefix}.findings`, 100);
      }
      if (macro.collectionMethod) {
        form.setFieldValue(
          `${fieldPrefix}.collectionMethod`,
          macro.collectionMethod
        );
        onCertaintyChange?.(`${fieldPrefix}.collectionMethod`, 100);
      }
      if (macro.material) {
        form.setFieldValue(`${fieldPrefix}.material`, macro.material);
        onCertaintyChange?.(`${fieldPrefix}.material`, 100);
      }
    },
    [form, fieldPrefix, onCertaintyChange]
  );

  const expandMacro = useCallback(
    (code: string): boolean => {
      if (!code.trim()) {
        setMacroError(false);
        return false;
      }
      const macro = specimenMacros.find(
        (m) => m.code.toLowerCase() === code.toLowerCase()
      );
      if (!macro) {
        setMacroError(true);
        return false;
      }
      applyMacro(macro);
      return true;
    },
    [specimenMacros, applyMacro]
  );

  const handleMacroChange = useCallback((val: string) => {
    setMacroInput(val.toUpperCase());
    setMacroError(false);
  }, []);

  const handleMacroKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab" || e.key === "Enter") {
        const expanded = expandMacro(macroInput);
        if (expanded) {
          e.preventDefault();
          onFocusNextMacro();
        } else if (e.key === "Enter") {
          e.preventDefault();
        }
      }
    },
    [macroInput, expandMacro, onFocusNextMacro]
  );

  const handleMacroOptionSubmit = useCallback(
    (val: string) => {
      const macro = specimenMacros.find(
        (m) => m.code.toLowerCase() === val.toLowerCase()
      );
      if (macro) {
        applyMacro(macro);
        onFocusNextMacro();
      }
    },
    [specimenMacros, applyMacro, onFocusNextMacro]
  );

  const handleSourceChange = useCallback(
    (value: string | null) => {
      newSubsiteNameRef.current = "";
      form.setFieldValue(`${fieldPrefix}.sourceId`, value);
      form.setFieldValue(`${fieldPrefix}.subsiteId`, null);
      const selectedSource = sources.find((s) => s.value === value);
      if (selectedSource?.defaultCollectionMethodName) {
        form.setFieldValue(
          `${fieldPrefix}.collectionMethod`,
          selectedSource.defaultCollectionMethodName
        );
        onCertaintyChange?.(`${fieldPrefix}.collectionMethod`, 90);
      }
      onCertaintyChange?.(`${fieldPrefix}.sourceId`, 100);
    },
    [form, fieldPrefix, sources, onCertaintyChange]
  );

  const handleSubsiteChange = useCallback(
    (value: string | null) => {
      newSubsiteNameRef.current = "";
      form.setFieldValue(`${fieldPrefix}.subsiteId`, value);
      onCertaintyChange?.(`${fieldPrefix}.subsiteId`, 100);
    },
    [fieldPrefix, form, onCertaintyChange]
  );

  const handleSubsiteInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const typedName = event.currentTarget.value.trim();
      const matchesExisting = availableSubsites.some(
        ({ label }) => label.toLowerCase() === typedName.toLowerCase()
      );
      newSubsiteNameRef.current = matchesExisting ? "" : typedName;
    },
    [availableSubsites]
  );

  const handleAddSubsiteClick = useCallback(() => {
    onAddSubsite?.(index, newSubsiteNameRef.current);
  }, [index, onAddSubsite]);

  const handleAddSuggestedSubsite = useCallback(() => {
    onAddSubsite?.(index, unmatchedSubsite || "");
  }, [index, onAddSubsite, unmatchedSubsite]);

  return (
    <Table.Tr>
      <Table.Td style={{ width: 64, verticalAlign: "middle" }}>
        {identifierEditable ? (
          <TextInput
            aria-label={`Specimen ${index + 1} identifier`}
            name={`${fieldPrefix}.number`}
            value={specimen.number || ""}
            onChange={(event) =>
              onIdentifierChange?.(event.currentTarget.value)
            }
            onBlur={(event) =>
              onIdentifierChange?.(event.currentTarget.value.trim())
            }
            required
            error={identifierError}
            size="xs"
            styles={{ input: { fontFamily: "monospace", fontWeight: 600 } }}
          />
        ) : (
          <Badge
            color="blue"
            variant="filled"
            size="md"
            style={{ fontFamily: "monospace" }}
          >
            {specimen.number}
          </Badge>
        )}
      </Table.Td>

      <Table.Td
        style={{ width: 120, verticalAlign: "top", padding: "6px 8px" }}
      >
        <Box mb={2} style={{ height: 14 }} />
        <Autocomplete
          ref={macroInputRef}
          value={macroInput}
          onChange={handleMacroChange}
          onKeyDown={handleMacroKeyDown}
          onOptionSubmit={handleMacroOptionSubmit}
          data={sanitizeOptions(macroSuggestions)}
          placeholder="Macro"
          size="xs"
          error={macroError}
          comboboxProps={{ width: 300, position: "bottom-start" }}
          styles={{
            input: {
              fontFamily: "monospace",
              fontWeight: 600,
              textTransform: "uppercase" as const,
              ...(macroError
                ? { borderColor: "var(--mantine-color-red-6)" }
                : {}),
            },
          }}
        />
      </Table.Td>

      <Table.Td style={{ verticalAlign: "top", padding: "6px 8px" }}>
        <CertaintySelect
          placeholder="Source"
          required
          showReviewBadge
          reviewTooltip="Verify anatomical source is correct"
          name={`${fieldPrefix}.sourceId`}
          data={sanitizeOptions(sources)}
          searchable
          size="xs"
          {...form.getInputProps(`${fieldPrefix}.sourceId`)}
          onChange={handleSourceChange}
          certainty={certainties[`${fieldPrefix}.sourceId`]}
          onCertaintyChange={(c: number) =>
            onCertaintyChange?.(`${fieldPrefix}.sourceId`, c)
          }
        />
      </Table.Td>

      <Table.Td style={{ verticalAlign: "top", padding: "6px 8px" }}>
        <CertaintySelect
          placeholder="Subsite"
          showReviewBadge
          reviewTooltip="Verify anatomical subsite is correct"
          data={sanitizeOptions(availableSubsites)}
          searchable
          clearable
          disabled={!specimen.sourceId}
          size="xs"
          {...form.getInputProps(`${fieldPrefix}.subsiteId`)}
          onChange={handleSubsiteChange}
          onInput={handleSubsiteInput}
          certainty={certainties[`${fieldPrefix}.subsiteId`]}
          onCertaintyChange={(c: number) =>
            onCertaintyChange?.(`${fieldPrefix}.subsiteId`, c)
          }
        />
        {unmatchedSubsite && !specimen.subsiteId && onAddSubsite && (
          <SmartSuggestion
            extractedValue={unmatchedSubsite}
            onAdd={handleAddSuggestedSubsite}
            isLoading={false}
          />
        )}
        {!unmatchedSubsite && onAddSubsite && (
          <Group justify="flex-end" mt={2}>
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconPlus size={10} />}
              onClick={handleAddSubsiteClick}
              styles={{ root: { fontSize: 10, padding: "0 4px" } }}
            >
              Add New
            </Button>
          </Group>
        )}
      </Table.Td>

      <Table.Td style={{ verticalAlign: "top", padding: "6px 8px" }}>
        <CertaintyTextInput
          placeholder="Findings from requisition"
          showReviewBadge
          reviewTooltip="Verify findings match requisition exactly"
          size="xs"
          {...form.getInputProps(`${fieldPrefix}.findings`)}
          certainty={certainties[`${fieldPrefix}.findings`]}
          onCertaintyChange={(c: number) =>
            onCertaintyChange?.(`${fieldPrefix}.findings`, c)
          }
          onBlur={onFindingsBlur}
        />
      </Table.Td>

      <Table.Td style={{ verticalAlign: "top", padding: "6px 8px" }}>
        <CertaintySelect
          placeholder="Method"
          showReviewBadge
          reviewTooltip="Verify collection method is correct"
          data={sanitizeOptions(collectionMethods)}
          searchable
          clearable
          size="xs"
          {...form.getInputProps(`${fieldPrefix}.collectionMethod`)}
          certainty={certainties[`${fieldPrefix}.collectionMethod`]}
          onCertaintyChange={(c: number) =>
            onCertaintyChange?.(`${fieldPrefix}.collectionMethod`, c)
          }
        />
        {unmatchedCollectionMethod && !specimen.collectionMethod ? (
          <SmartSuggestion
            extractedValue={unmatchedCollectionMethod}
            onAdd={onSmartAddCollectionMethod || onAddCollectionMethod}
          />
        ) : (
          <Group justify="flex-end" mt={2}>
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconPlus size={10} />}
              onClick={onAddCollectionMethod}
              styles={{ root: { fontSize: 10, padding: "0 4px" } }}
            >
              Add New
            </Button>
          </Group>
        )}
      </Table.Td>

      <Table.Td style={{ verticalAlign: "top", padding: "6px 8px" }}>
        <CertaintySelect
          placeholder="Material"
          showReviewBadge
          reviewTooltip="Verify how the specimen was physically received"
          data={materialOptions}
          searchable
          clearable
          size="xs"
          {...form.getInputProps(`${fieldPrefix}.material`)}
          certainty={certainties[`${fieldPrefix}.material`]}
          onCertaintyChange={(c: number) =>
            onCertaintyChange?.(`${fieldPrefix}.material`, c)
          }
        />
      </Table.Td>

      <Table.Td
        style={{ width: 40, verticalAlign: "middle", textAlign: "center" }}
      >
        <Group gap={4} justify="center" wrap="nowrap">
          {canDelete && (
            <ActionIcon
              color="red"
              variant="subtle"
              size="sm"
              onClick={onDeleteClick}
              title="Delete specimen"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
